import React from 'react';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Modal,
  TextField,
} from '@material-ui/core';
import { getAllTrainingCertificatesAsync } from './CertificationStore/CertificationStoreSlice';
import { useDispatch, useSelector } from 'react-redux';
import BuzzPagination from '../../../components/BuzzPagination/BuzzPagination';
import { toast } from 'react-toastify';
import { certificationColumn } from './columns';
import AddTrainingCertificate from './AddTrainingCertificate';
import EditTrainingCertificate from './EditTrainingCertificate';

const BUSINESS_VERTICALS = [
  { value: 'atheneum', label: 'Atheneum' },
  { value: 'iimtt', label: 'IIMTT' },
];

// Adjust the range as needed — currently covers the last 20 years through
// 5 years ahead, which comfortably covers past + upcoming certificates.
const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear + 5; y >= currentYear - 20; y--) {
    years.push(y);
  }
  return years;
};

const YEAR_OPTIONS = buildYearOptions();

const initialFilters = {
  studentName: '',
  awardYear: '',
  businessVertical: 'atheneum',
};

const DEBOUNCE_MS = 500;

const Certification = () => {
  const [open, setOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [filters, setFilters] = React.useState(initialFilters);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const debounceRef = React.useRef(null);
  const isFirstRender = React.useRef(true);

  const dispatch = useDispatch();
  const { certificates, loading } = useSelector((state) => {
    const { trainingCertificateReducer } = state;

    return {
      certificates:
        (trainingCertificateReducer &&
          trainingCertificateReducer.certificates) ||
        [],
      loading: trainingCertificateReducer && trainingCertificateReducer.loading,
    };
  });

  const buildParams = (currentFilters) => {
    const params = {};
    Object.keys(currentFilters).forEach((key) => {
      if (currentFilters[key] !== '' && currentFilters[key] !== null) {
        params[key] = currentFilters[key];
      }
    });
    return params;
  };

  const fetchCertificates = async (currentFilters) => {
    try {
      dispatch(
        getAllTrainingCertificatesAsync({ params: buildParams(currentFilters) })
      )
        .unwrap()
        .catch((err) => {
          toast.error(
            (err && err.message) || 'Certificates could not be fetched.'
          );
        });
    } catch (error) {
      toast.error('Certificates could not be fetched. Please try again!');
    }
  };

  React.useEffect(() => {
    fetchCertificates(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchCertificates(filters);
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  const handleAddSuccess = () => {
    handleClose();
    fetchCertificates(filters);
  };

  const handleEditSuccess = () => {
    setEditRow(null);
    fetchCertificates(filters);
  };

  const handlePageChange = (val) => {
    if (val !== page) {
      setPage(val);
      dispatch(
        getAllTrainingCertificatesAsync({
          params: { ...buildParams(filters), page: val, limit: 10 },
        })
      )
        .unwrap()
        .catch((err) => {
          toast.error(err.message);
        });
    }
  };

  const columns = React.useMemo(
    () =>
      certificationColumn(
        () => fetchCertificates(filters),
        (row) => setEditRow(row),
        filters && filters.businessVertical
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters]
  );

  return (
    <Box>
      <Box textAlign={'right'} mb={2}>
        <Button onClick={handleOpen} variant="contained" color="primary">
          Add Certificate
        </Button>
      </Box>
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search by Student Name"
              variant="outlined"
              size="small"
              value={filters.studentName}
              onChange={handleFilterChange('studentName')}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              label="Award Year"
              variant="outlined"
              size="small"
              value={filters.awardYear}
              onChange={handleFilterChange('awardYear')}>
              <MenuItem value="">All Years</MenuItem>
              {YEAR_OPTIONS.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              label="Business Vertical"
              variant="outlined"
              size="small"
              value={filters.businessVertical}
              onChange={handleFilterChange('businessVertical')}>
              {BUSINESS_VERTICALS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="outlined" onClick={clearFilters}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box mb={4}>
        <BuzzTable
          columns={columns}
          data={certificates && certificates.data}
          loading={loading}
        />
        <Box display="flex" justifyContent="center" pb={3}>
          <BuzzPagination
            onClick={handlePageChange}
            total={certificates && certificates.total}
            pageNo={certificates && certificates.page}
            defaultPage={1}
            perPage={10}
          />
        </Box>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <AddTrainingCertificate onSuccess={handleAddSuccess} />
      </Modal>

      <Modal open={!!editRow} onClose={() => setEditRow(null)}>
        {editRow ? (
          <EditTrainingCertificate
            certificate={editRow}
            onSuccess={handleEditSuccess}
            onClose={() => setEditRow(null)}
          />
        ) : (
          <div />
        )}
      </Modal>
    </Box>
  );
};

export default Certification;
